import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Lock, ShieldCheck } from 'lucide-react';

export function Payment() {
    const navigate = useNavigate();
    const { upgradeToPro } = useAuth();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);

    const plan = searchParams.get('plan') || 'pro';
    const price = plan === 'api' ? '$0.02/req' : '$9.99/mo';

    const handlePayment = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        // Simulate API call to Payment Gateway (Razorpay/Stripe)
        setTimeout(() => {
            upgradeToPro();
            setLoading(false);
            navigate('/dashboard');
            alert("Payment Successful! You are now a Pro member.");
        }, 2000);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                    Complete your purchase
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    You are subscribing to the <span className="font-semibold text-blue-600 uppercase">{plan}</span> plan for <span className="font-bold">{price}</span>
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <form className="space-y-6" onSubmit={handlePayment}>
                        {/* Dummy Card Details */}
                        <div>
                            <label htmlFor="card-number" className="block text-sm font-medium text-gray-700">
                                Card Number
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CreditCard className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="card-number"
                                    id="card-number"
                                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 px-3 ring-1 ring-inset ring-gray-300"
                                    placeholder="0000 0000 0000 0000"
                                    defaultValue="4242 4242 4242 4242"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="expiry" className="block text-sm font-medium text-gray-700">
                                    Expiration Date
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        name="expiry"
                                        id="expiry"
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 ring-1 ring-inset ring-gray-300"
                                        placeholder="MM/YY"
                                        defaultValue="12/26"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="cvc" className="block text-sm font-medium text-gray-700">
                                    CVC
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        name="cvc"
                                        id="cvc"
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 ring-1 ring-inset ring-gray-300"
                                        placeholder="123"
                                        defaultValue="123"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <ShieldCheck className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                        This is a <span className="font-bold">Dummy Payment</span> for testing. No money will be deducted.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">Processing... <Lock className="w-4 h-4" /></span>
                                ) : (
                                    <span className="flex items-center gap-2">Pay {price} <Lock className="w-4 h-4" /></span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
